import NepaliDate from 'nepali-date-converter'

/**
 * NEPSE's zone. Every market or API clock is shown in it, never the device's:
 * a reader in New York looking at an 11:00 open must still see 11:00.
 */
export const KATHMANDU_TZ = 'Asia/Kathmandu'

const KATHMANDU_DATE = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: KATHMANDU_TZ,
})

const adDateParts = (value: string): [number, number, number] | null => {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (dateOnly !== null) {
    const year = Number(dateOnly[1])
    const month = Number(dateOnly[2])
    const day = Number(dateOnly[3])
    const valid = new Date(Date.UTC(year, month - 1, day))
    return valid.getUTCFullYear() === year &&
      valid.getUTCMonth() === month - 1 &&
      valid.getUTCDate() === day
      ? [year, month, day]
      : null
  }

  const timestamp = new Date(value)
  if (Number.isNaN(timestamp.getTime())) return null
  const parts = KATHMANDU_DATE.formatToParts(timestamp)
  const number = (type: string) => Number(parts.find((part) => part.type === type)?.value)
  return [number('year'), number('month'), number('day')]
}

/** Convert an API AD date or timestamp to a Kathmandu-local BS calendar date. */
export const toBsDate = (value: string | null): NepaliDate | null => {
  if (value === null) return null
  const parts = adDateParts(value)
  if (parts === null) return null
  const [year, month, day] = parts
  try {
    // The converter reads local Date fields. Construct from the extracted
    // Kathmandu calendar day, never directly from an arbitrary UTC timestamp.
    return NepaliDate.fromAD(new Date(year, month - 1, day))
  } catch {
    // The library uses a finite BS calendar table; do not display AD as BS.
    return null
  }
}

export const formatBsDate = (value: string | null): string =>
  toBsDate(value)?.format('D MMMM YYYY', 'en') ?? '—'

/** Compact calendar tile, e.g. "30 BH" for 30 Bhadra. */
export const formatBsDateShort = (value: string | null): string => {
  const date = toBsDate(value)
  return date === null
    ? '—'
    : `${date.format('D', 'en')} ${date.format('MMMM', 'en').slice(0, 2).toUpperCase()}`
}

/**
 * "1 Bhadra 2083" (a BS date mm-be already spells out) → "Bhadra 1" in the current BS year, as
 * Public Issues and Home's issues card print it; any other year keeps it ("Bhadra 1, 2082"), since
 * a bare "Bhadra 1" on an older row could be any year. A string that does not split into day, month
 * and year comes back as it was; null is "—".
 */
export const shortBsLabel = (value: string | null): string => {
  if (value === null) return '—'
  const [day, month, year] = value.split(' ')
  if (day === undefined || month === undefined || year === undefined) return value
  const thisYear = toBsDate(new Date().toISOString())?.getYear()
  return thisYear === Number(year) ? `${month} ${day}` : `${month} ${day}, ${year}`
}

const AD_SHORT = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' })

/**
 * "7 Bhadra (23 Aug)" — Market Events' row date: the BS day a member reads a
 * notice by, with the AD day it falls on beside it. No year on either
 * calendar, matching `PeriodPicker`'s own `bsShort` — every caller so far
 * reads a near-term notice inside the current BS year, and a year on a
 * corporate-calendar row would be noise the frame does not draw.
 *
 * Reuses `toBsDate` for the AD→BS conversion (never a second one) and this
 * module's own `adDateParts` for the AD side, so both halves read the same
 * Kathmandu-local day even when `value` carries a time component.
 */
export const formatBsWithAd = (value: string | null): string => {
  const bs = toBsDate(value)
  const parts = value === null ? null : adDateParts(value)
  if (bs === null || parts === null) return '—'
  const [year, month, day] = parts
  return `${bs.format('D MMMM', 'en')} (${AD_SHORT.format(new Date(year, month - 1, day))})`
}

/**
 * Whole days from `from` (default now) to a BS date mm-be spelled out ("4 Bhadra 2083"), counted
 * on the Kathmandu calendar: 0 today, 1 tomorrow. Null when the label is not within the next
 * `LOOKAHEAD_DAYS` days or is not one this formatter spells the same way — the caller then draws
 * no countdown rather than a wrong one. Found by stepping forward and comparing the formatted day,
 * so it needs no month-name table of its own.
 */
const LOOKAHEAD_DAYS = 120
const DAY_MS = 86_400_000

export const daysUntilBs = (value: string | null, from: Date = new Date()): number | null => {
  if (value === null) return null
  for (let days = 0; days <= LOOKAHEAD_DAYS; days += 1) {
    const day = toBsDate(new Date(from.getTime() + days * DAY_MS).toISOString())
    if (day?.format('D MMMM YYYY', 'en') === value) return days
  }
  return null
}
